/**
 * Maps server payloads onto the shapes the Phase 1-4 components already render.
 *
 * The API and the UI deliberately do not share field names - `message` vs `text`,
 * `otherPartyDisplayName` vs `name`, snake_case on the orders endpoint. Keeping
 * that translation in one file means the components stay untouched as the API
 * evolves.
 */

/** Matches getUsersLastActiveMap() in chat.service.ts - a 5 minute window. */
export const ONLINE_WINDOW_MS = 5 * 60 * 1000;

/**
 * The chat client runs on chat.z2market.com while uploads are served by the main
 * site at /uploads/... (Express static). The subdomain does not proxy /uploads,
 * so a stored value like "/uploads/avatars/x.jpg" would 404 from here.
 *
 * Absolute URLs (R2) and data:/blob: URLs pass through untouched.
 */
export const MAIN_SITE_ORIGIN = import.meta.env?.VITE_MAIN_SITE_ORIGIN || 'https://z2market.com';

export function absoluteAssetUrl(url) {
  if (!url) return null;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  return `${MAIN_SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function isOnline(lastSeenAt) {
  if (!lastSeenAt) return false;
  const timestamp = new Date(lastSeenAt).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp < ONLINE_WINDOW_MS;
}

/**
 * GET /api/chat/conversations returns one row per COUNTERPARTY (the server
 * deduplicates), with `conversationId` being the most recent thread with them.
 */
export function mapConversation(row) {
  const unreadCount = row.unreadCount ?? 0;

  return {
    id: row.conversationId,
    partnerId: row.otherPartyId ?? null,
    name: row.otherPartyDisplayName || row.otherPartyUsername || 'Unknown user',
    username: row.otherPartyUsername || null,
    // storeAvatarUrl first - that is the field the rest of the platform treats
    // as authoritative (navbar.js, listings.js). Falls back to `avatar`.
    avatarUrl: absoluteAssetUrl(row.otherPartyStoreAvatar || row.otherPartyAvatar),
    partnerRole: row.otherPartyRole ?? 'BUYER',
    // Mapped for completeness, but NO chat surface renders it any more: the
    // country was removed from the header and the details panel by owner
    // request. The API still returns otherPartyCountry - see lib/peerFacts.js
    // for what the chat shows in its place.
    country: row.otherPartyCountry || null,
    verified: Boolean(row.otherPartyTopSeller || row.otherPartyIsOfficial),
    officialTitle: row.otherPartyOfficialTitle || null,
    topSeller: Boolean(row.otherPartyTopSeller),
    lastMessage: row.lastMessagePreview || 'No messages yet',
    lastMessageAt: row.lastMessageAt ? new Date(row.lastMessageAt) : null,
    unreadCount,
    unread: unreadCount > 0,
    pinned: Boolean(row.pinned),
    online: isOnline(row.otherUserLastSeenAt),
    lastSeenAt: row.otherUserLastSeenAt ? new Date(row.otherUserLastSeenAt) : null,
    listingId: row.listingId ?? null,
    listingTitle: row.listingTitle ?? null,
    orderNumber: row.orderNumber ?? null,
    orderId: row.orderId ?? null,
    conversationCount: row.conversationCount ?? 1,
  };
}

/**
 * Which read timestamp proves the OTHER party has seen one of my messages:
 * a buyer's message is "seen" once `readBySellerAt` is set, and vice versa.
 */
function otherPartyReadAt(row) {
  return row.senderRole === 'BUYER' ? row.readBySellerAt : row.readByBuyerAt;
}

export function mapMessage(row, currentUserId) {
  const mine = row.senderId === currentUserId;
  const seen = mine && Boolean(otherPartyReadAt(row));

  return {
    id: row.id,
    mine,
    senderId: row.senderId,
    senderName: row.senderDisplayName || 'Unknown',
    senderRole: row.senderRole,
    messageType: row.messageType,
    metadata: row.metadata ?? null,
    text: row.message ?? '',
    imageUrl: absoluteAssetUrl(row.imageUrl),
    images: row.imageUrl ? [absoluteAssetUrl(row.imageUrl)] : [],
    at: new Date(row.createdAt),
    isDeleted: Boolean(row.isDeleted),
    /* Only two states exist server-side: sent, and read (seen). There is no
       delivery receipt, so the UI never claims "delivered". */
    status: mine ? (seen ? 'seen' : 'sent') : null,
  };
}

/** GET /api/orders/chat-context - snake_case rows. */
export function mapOrder(row) {
  return {
    id: row.id,
    orderNumber: row.order_number,
    item: row.listing_title || 'Order',
    status: row.status,
    price: Number(row.total ?? 0),
    at: new Date(row.updated_at ?? row.createdAt),
    // Which side of the order the signed-in user is on. Older servers do not
    // send these yet, so a null here means "fall back to the account role".
    buyerId: row.buyer_id ?? null,
    sellerId: row.seller_id ?? null,
  };
}

/** GET /api/listings?sellerId= - strings for price, images as an array. */
export function mapListing(row) {
  let images = [];
  try {
    images = Array.isArray(row.images) ? row.images : JSON.parse(row.images || '[]');
  } catch {
    images = [];
  }

  return {
    id: row.id,
    displayId: row.displayId,
    slug: row.slug,
    title: row.title,
    blurb: row.description || '',
    price: Number(row.price ?? 0),
    thumb: absoluteAssetUrl(images[0]),
    stock: row.stock ?? 0,
    href: `/offer/${row.displayId}/${row.slug}`,
  };
}

/** Total unread messages across the inbox - used for the bell badge. */
export function totalUnread(conversations) {
  return conversations.reduce((sum, conversation) => sum + (conversation.unreadCount ?? 0), 0);
}
